import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { r1Api } from "../../services/api";
import type {
  CreateValidationEventPayload,
  ValidationEvent,
  ValidationStep,
} from "../domain/types";

const STEP_ORDER: Record<ValidationStep, number> = {
  step_1: 1,
  step_2: 2,
  step_3: 3,
};

export function useValidationState(
  fundId: number,
  quarter: string,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const queryKey = ["r1_validation_events", fundId, quarter];

  const { data: events = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => r1Api.getValidationCurrentState(fundId, quarter),
    enabled: enabled && !!fundId && !!quarter,
  });

  const { mutate: submitEvent, isPending: isSubmitting } = useMutation({
    mutationFn: (payload: CreateValidationEventPayload) =>
      r1Api.createValidationEvent(payload),
    onSuccess: (newEvent) => {
      queryClient.setQueryData<ValidationEvent[]>(queryKey, (prev = []) => {
        const filtered = prev.filter((e) => e.step !== newEvent.step);
        return [...filtered, newEvent].sort(
          (a, b) => (STEP_ORDER[a.step] ?? 0) - (STEP_ORDER[b.step] ?? 0),
        );
      });
      queryClient.invalidateQueries({
        queryKey: ["r1", "positions-issuers-reconciliation-audit"],
      });
      queryClient.invalidateQueries({ queryKey: ["r1", "funds-portfolio"] });
    },
  });

  const latestStatusForStep = (step: "step_1" | "step_3") =>
    events
      .filter((e) => e.step === step)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
      ?.status;

  const quantiliaLocked = latestStatusForStep("step_1") === "validated";
  const r1Locked = latestStatusForStep("step_3") === "validated";

  return { events, isLoading, submitEvent, isSubmitting, quantiliaLocked, r1Locked };
}
